import { Navigate, Route, Routes } from "react-router-dom";
import {
  AuthProvider,
  SUPER_ADMIN_EMAIL,
  useAuth,
} from "./auth/AuthProvider";
import PublicLayout from "./layouts/PublicLayout";
import AdminLayout from "./layouts/AdminLayout";

// Public
import Landing from "./pages/public/Landing";
import Terms from "./pages/public/Terms";
import Privacy from "./pages/public/Privacy";
import Support from "./pages/public/Support";
import DeleteAccount from "./pages/public/DeleteAccount";

// Admin
import AdminLogin from "./pages/admin/Login";
import Dashboard from "./pages/admin/Dashboard";
import Reports from "./pages/admin/Reports";
import Users from "./pages/admin/Users";
import UserDetail from "./pages/admin/UserDetail";
import Flagged from "./pages/admin/Flagged";
import Rooms from "./pages/admin/Rooms";
import Rejected from "./pages/admin/Rejected";
import Quiz from "./pages/admin/Quiz";
import Roles from "./pages/admin/Roles";
import BotSettings from "./pages/admin/BotSettings";

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route index element={<Landing />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
        </Route>

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <AdminGuard>
              <AdminLayout />
            </AdminGuard>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="reports" element={<Reports />} />
          <Route path="users" element={<Users />} />
          <Route path="users/:uid" element={<UserDetail />} />
          <Route path="flagged" element={<Flagged />} />
          <Route path="rooms" element={<Rooms />} />
          <Route path="quiz" element={<Quiz />} />
          <Route path="rejected" element={<Rejected />} />
          <Route path="bot" element={<BotSettings />} />
          <Route path="roles" element={<Roles />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
  );
}

function AdminGuard({ children }: { children: JSX.Element }) {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!isAdmin) return <NotAuthorized />;
  return children;
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="h-10 w-10 rounded-full border-2 border-primarySoft border-t-transparent animate-spin" />
    </div>
  );
}

/**
 * Yetkisi olmayan hesabın gördüğü ekran.
 *
 * Eskiden burada "kendine yetki ver" düğmesi vardı; custom claim yazma yolu
 * bu projede çalışmadığı için hiçbir zaman iş görmedi. Artık yetki
 * `admins/{uid}` belgesiyle veriliyor ve o belgeyi yalnızca proje sahibi
 * yazabiliyor — o yüzden burada yapılacak tek doğru şey, kişinin sahibe
 * iletmesi gereken kimliğini göstermek.
 */
function NotAuthorized() {
  const { signOut, user } = useAuth();

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="card p-10 max-w-md w-full text-center">
        <div className="text-5xl mb-4">🚫</div>
        <h1 className="text-2xl font-extrabold mb-2">Yetkin yok</h1>
        <p className="text-textSecondary mb-6">
          Bu hesap yönetici değil. Panele girmek için proje sahibinin{" "}
          <b>Yetkiler</b> sayfasından hesabına yetki vermesi gerekiyor.
        </p>

        <div className="rounded-xl bg-surfaceHigh border border-stroke p-4 mb-6 text-left">
          <div className="section-label mb-1">Hesabın</div>
          <div className="text-sm font-semibold break-all">
            {user?.email ?? "—"}
          </div>
          <div className="section-label mt-3 mb-1">UID</div>
          <div className="text-xs font-mono break-all text-textSecondary">
            {user?.uid ?? "—"}
          </div>
          <p className="text-[11px] text-textMuted mt-3">
            Bu iki bilgiden birini {SUPER_ADMIN_EMAIL} adresine ilet. Yetki
            verildiği anda bu sayfa kendiliğinden açılır.
          </p>
        </div>

        <button className="btn-outline w-full" onClick={signOut}>
          Çıkış Yap
        </button>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center">
        <div className="text-6xl mb-4">🤷</div>
        <h1 className="text-2xl font-extrabold">Sayfa bulunamadı</h1>
        <a href="/" className="btn-ghost mt-6 inline-flex">Ana sayfaya dön</a>
      </div>
    </div>
  );
}
